'use client';

import './DateTimePicker.scss';

import { cn } from '@core/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Calendar } from 'react-calendar';

import SvgCalendarBlank from '../../../__generated__/icon/SvgCalendarBlank';
import SvgChevronleft from '../../../__generated__/icon/SvgChevronleft';
import SvgChevronright from '../../../__generated__/icon/SvgChevronright';
import SvgChevronrightDouble from '../../../__generated__/icon/SvgChevronrightDouble';
import SvgChevronrleftDouble from '../../../__generated__/icon/SvgChevronrleftDouble';
import SvgModalclose from '../../../__generated__/icon/SvgModalclose';

type CalendarValuePiece = Date | null;
type CalendarValue =
  CalendarValuePiece | [CalendarValuePiece, CalendarValuePiece];

import { Input } from '../Input/Input';

interface DateTimePickerProps {
  /** YYYY-MM-DDTHH:mm:ss 또는 YYYY-MM-DDTHH:mm */
  value: string | null;
  onChange: (datetime: string) => void;
  placeholder?: string;
  className?: string;
  /** YYYY-MM-DD */
  minDate?: string;
  /** YYYY-MM-DD */
  maxDate?: string;
  disabled?: boolean;
  contentClassName?: string;
  onClear?: () => void;
  /** 시간 포맷 (기본값: 'HH:mm:ss') */
  timeFormat?: 'HH:mm' | 'HH:mm:ss';
  /** 날짜 시간 포맷 (기본값: 'YYYY-MM-DDTHH:mm:ss') */
  format?: string;
}

export const DateTimePicker = ({
  value,
  onChange,
  placeholder,
  className,
  minDate,
  maxDate,
  disabled,
  contentClassName,
  onClear,
  timeFormat = 'HH:mm',
  format = 'YYYY-MM-DDTHH:mm:ss',
}: DateTimePickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? dayjs(value).toDate() : null,
  );
  const [timeValue, setTimeValue] = useState(
    value ? dayjs(value).format(timeFormat) : '',
  );

  const outerValue = value ? dayjs(value).format(format) : '';
  const [inputValue, setInputValue] = useState(outerValue);

  useEffect(() => {
    const formattedValue = value ? dayjs(value).format(format) : '';
    setInputValue(formattedValue);
    setSelectedDate(value ? dayjs(value).toDate() : null);
    setTimeValue(value ? dayjs(value).format(timeFormat) : '');
  }, [value, format, timeFormat]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    // 값을 지우는건 onClear 버튼으로만 가능하도록
    if (!inputValue) {
      setInputValue(outerValue);

      return;
    }

    if (!dayjs(inputValue).isValid()) {
      setInputValue(outerValue);

      return;
    }

    onChange(dayjs(inputValue).format(format));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const handleDateChange = (nextValue: CalendarValue) => {
    const date = Array.isArray(nextValue) ? nextValue[0] : nextValue;
    if (!date) return;

    const newDate = dayjs(date);
    setSelectedDate(date);

    // 시간이 설정되어 있으면 기존 시간 유지, 없으면 현재 시간 사용
    let finalDateTime;
    if (timeValue) {
      const [hours, minutes, seconds] = timeValue.split(':');
      finalDateTime = newDate
        .hour(Number.parseInt(hours))
        .minute(Number.parseInt(minutes))
        .second(seconds ? Number.parseInt(seconds) : 0);
    } else {
      finalDateTime = newDate.hour(0).minute(0).second(0);
    }

    onChange(finalDateTime.format(format));
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeValue = e.target.value;
    setTimeValue(newTimeValue);

    if (selectedDate && newTimeValue) {
      const [hours, minutes, seconds] = newTimeValue.split(':');
      const finalDateTime = dayjs(selectedDate)
        .hour(Number.parseInt(hours))
        .minute(Number.parseInt(minutes))
        .second(seconds ? Number.parseInt(seconds) : 0);

      onChange(finalDateTime.format(format));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative flex items-center', className)}>
        <Input
          type="text"
          className={cn('w-200 min-w-fit', className)}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          aria-label="날짜 시간 입력"
          rightContent={
            <div className="flex items-center gap-4">
              {value && onClear ? (
                <button
                  type="button"
                  aria-label="입력값 지우기"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear?.();
                  }}
                >
                  <SvgModalclose
                    className="size-12 text-Gray-700"
                    originalFill
                  />
                </button>
              ) : null}
              <PopoverTrigger asChild disabled={disabled}>
                <button
                  type="button"
                  className="ml-8"
                  aria-label="달력 열기"
                  tabIndex={-1}
                >
                  <SvgCalendarBlank className="size-12 text-Gray-700" />
                </button>
              </PopoverTrigger>
            </div>
          }
        />
      </div>
      <PopoverContent className={cn('w-fit', contentClassName)}>
        <div className="mt-8 rounded-small border border-Gray-200 bg-Gray-white p-16 shadow-lg">
          {/* 달력 */}
          <Calendar
            className="common-datetime-picker"
            maxDate={maxDate ? new Date(maxDate) : undefined}
            minDate={minDate ? new Date(minDate) : undefined}
            next2Label={<SvgChevronrightDouble />}
            nextLabel={<SvgChevronright />}
            prev2Label={<SvgChevronrleftDouble />}
            prevLabel={<SvgChevronleft />}
            value={selectedDate}
            onChange={handleDateChange}
          />

          {/* 시간 선택 */}
          <div className="mt-12 border-t border-Gray-200 pt-12">
            <div className="mb-8 font-500 text-Gray-500 typo-caption2">
              시간 선택
            </div>
            <div className="flex items-center gap-8">
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                step={timeFormat === 'HH:mm:ss' ? '1' : '60'}
                className="h-32 w-full rounded-xsmall border border-Gray-300 px-8 text-center font-500 text-Gray-900 typo-body4 focus:border-Gray-900 focus:outline-none"
              />
              <div className="shrink-0 text-Gray-400 typo-caption2">
                {timeFormat === 'HH:mm:ss' ? '시:분:초' : '시:분'}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
